/**
 * Meta (Facebook) Pixel integration helper.
 * Provides type-safe wrappers around window.fbq for standard e-commerce events.
 * Safe to call in both SSR and client environments.
 */

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}

export function isFbqAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.fbq === "function";
}

/**
 * Fires standard PageView event.
 */
export function trackMetaPageView(): void {
  if (isFbqAvailable()) {
    window.fbq!("track", "PageView");
  }
}

/**
 * Fires ViewContent when a user visits a product details page.
 */
export function trackMetaViewContent(product: {
  id: string;
  name: string;
  price: number;
  category?: string;
  currency?: string;
}): void {
  if (isFbqAvailable()) {
    window.fbq!("track", "ViewContent", {
      content_name: product.name,
      content_ids: [product.id],
      content_type: "product",
      value: product.price,
      currency: product.currency || "INR",
      content_category: product.category,
    });
  }
}

/**
 * Fires AddToCart when an item is added to the cart.
 */
export function trackMetaAddToCart(item: {
  id: string;
  name: string;
  price: number;
  qty?: number;
  currency?: string;
}): void {
  if (isFbqAvailable()) {
    window.fbq!("track", "AddToCart", {
      content_name: item.name,
      content_ids: [item.id],
      content_type: "product",
      value: (item.price || 0) * (item.qty || 1),
      currency: item.currency || "INR",
    });
  }
}

/**
 * Fires InitiateCheckout when a user opens the checkout modal or navigates to /checkout.
 */
export function trackMetaInitiateCheckout(payload: {
  items: Array<{ id: string; name?: string; price: number; qty?: number }>;
  total: number;
  currency?: string;
}): void {
  if (isFbqAvailable()) {
    window.fbq!("track", "InitiateCheckout", {
      content_ids: payload.items.map((i) => i.id),
      content_type: "product",
      num_items: payload.items.reduce((acc, curr) => acc + (curr.qty || 1), 0),
      value: payload.total,
      currency: payload.currency || "INR",
    });
  }
}

/**
 * Fires Purchase event upon successful payment confirmation.
 */
export function trackMetaPurchase(payload: {
  orderId: string;
  total: number;
  currency?: string;
  items?: Array<{ id: string; name?: string; price: number; qty?: number }>;
}): void {
  if (isFbqAvailable()) {
    window.fbq!("track", "Purchase", {
      order_id: payload.orderId,
      value: payload.total,
      currency: payload.currency || "INR",
      content_type: "product",
      content_ids: payload.items?.map((i) => i.id) || [],
      num_items: payload.items?.reduce((acc, curr) => acc + (curr.qty || 1), 0),
    });
  }
}
